// Typed fetch wrapper. Every API call goes through here so timeouts, error
// shape, and auth headers live in one place. Responses are not validated at
// runtime — callers declare the expected response type via the type parameter
// and trust the platform contract.

import * as Sentry from "@sentry/react";
import { newRequestId } from "@/lib/sentry";

const BASE_URL = (import.meta.env.VITE_PLATFORM_BASE_URL as string | undefined) ?? "";

// Resolve a platform-relative path against the API origin. apiFetch does this
// internally; long-lived streams (SSE) that bypass apiFetch must use this so
// they hit the platform (api.gated.cc) and not the dashboard's own CloudFront
// origin — a bare relative `/api/...` resolves to app.gated.cc, whose SPA
// fallback returns index.html with 200 and silently swallows the stream.
export function platformUrl(path: string): string {
  return `${BASE_URL}${path}`;
}

type TokenGetter = () => Promise<string | null>;
let getToken: TokenGetter = async () => null;

export function setAuthTokenGetter(fn: TokenGetter): void {
  getToken = fn;
}

export function getAuthToken(): Promise<string | null> {
  return getToken();
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  // x-request-id sent with the call — correlates this failure with the
  // platform's Sentry event + logs for the same request.
  readonly requestId: string;

  constructor(status: number, body: unknown, message: string, requestId: string) {
    super(message);
    this.status = status;
    this.body = body;
    this.requestId = requestId;
    this.name = "ApiError";
  }
}

function throwApiError(
  status: number,
  statusText: string,
  body: unknown,
  requestId: string,
): never {
  const message =
    (typeof body === "object" && body !== null && "message" in body
      ? String((body as Record<string, unknown>).message)
      : undefined) ?? `API ${status} ${statusText}`;
  throw new ApiError(status, body, message, requestId);
}

export async function apiFetch<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 15_000);
  const requestId = newRequestId();
  try {
    const token = await getToken();
    const res = await fetch(`${BASE_URL}${path}`, {
      ...init,
      signal: init?.signal ?? ctrl.signal,
      headers: {
        accept: "application/json",
        "x-request-id": requestId,
        ...(init?.body ? { "content-type": "application/json" } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
    });

    recordRequestBreadcrumb(init?.method, path, res.status, requestId);

    let body: unknown = null;
    const text = await res.text();
    if (text) {
      try {
        body = JSON.parse(text);
      } catch {
        body = text;
      }
    }

    if (!res.ok) throwApiError(res.status, res.statusText, body, requestId);

    return body as T;
  } finally {
    clearTimeout(timer);
  }
}

// Leaves a trail of API calls (with their request ids) on the Sentry scope so
// any later error shows what the browser asked the platform just before.
function recordRequestBreadcrumb(
  method: string | undefined,
  path: string,
  status: number,
  requestId: string,
): void {
  Sentry.addBreadcrumb({
    category: "fetch",
    type: "http",
    level: status >= 500 ? "error" : "info",
    data: {
      method: method ?? "GET",
      path,
      status_code: status,
      request_id: requestId,
    },
  });
}

// Binary variant of apiFetch: returns Blob instead of parsing JSON, same auth and error shape.
export async function apiFetchBlob(
  path: string,
  init?: RequestInit,
): Promise<Blob> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 30_000);
  const requestId = newRequestId();
  try {
    const token = await getToken();
    const res = await fetch(`${BASE_URL}${path}`, {
      ...init,
      signal: init?.signal ?? ctrl.signal,
      headers: {
        "x-request-id": requestId,
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
    });

    recordRequestBreadcrumb(init?.method, path, res.status, requestId);

    if (!res.ok) {
      const text = await res.text();
      let body: unknown = text;
      try {
        body = JSON.parse(text);
      } catch {
        // non-JSON error body — keep the raw text
      }
      throwApiError(res.status, res.statusText, body, requestId);
    }

    return await res.blob();
  } finally {
    clearTimeout(timer);
  }
}
